import { plural } from '@ipfabric/frontend-shared-assets/utils/plural';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { createTextColumn } from '@ipfabric/utils';
import { unsafeAssert } from '@ipfabric/utils/types/unsafeAssert';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { Table } from 'components/Table/components/Table';
import type { TableBulkAction } from 'components/Table/model/TableModel';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { AimIntegrationDraft } from '../../../model/aimDrafts';
import { AimIntegrationDialog } from '../dialogs/AimIntegrationDialog';
import { useAimRowActions } from './helpers/useAimRowActions';

type Props = {
  form: UseFormReturn<{ aimIntegrations: AimIntegrationDraft[] }>;
};

export const AimIntegrationsTable = ({ form }: Props) => {
  const integrations = useWatch({
    control: form.control,
    name: 'aimIntegrations',
  });

  const actions = useAimRowActions<AimIntegrationDraft>({
    fieldName: 'aimIntegrations' as never,
    control: form.control as never,
    setValue: form.setValue as never,
    isReferenced: () => [],
    deleteConfirmLabel: (count) => ({
      header: `${count} ${plural(count, 'integration')} will be deleted`,
      subHeader:
        count === 1
          ? 'Do you really want to delete this integration?'
          : `Do you really want to delete ${count} selected integrations?`,
      btnConfirm: 'Delete',
      btnSecondaryConfirm: 'Cancel',
    }),
    blockedDeleteLabel: () => ({
      header: 'Cannot delete',
      subHeader: 'Integration is referenced.',
      btnConfirm: 'OK',
    }),
  });

  const columns = useMemo(
    () => [
      createTextColumn<AimIntegrationDraft>({
        key: 'platform',
        title: 'Platform',
      }),
      createTextColumn<AimIntegrationDraft>({
        key: 'apiUrl',
        title: 'API URL',
      }),
      createTextColumn<AimIntegrationDraft>({
        key: 'apiKey',
        title: 'API key',
      }),
      createTextColumn<AimIntegrationDraft>({ key: 'slug', title: 'Slug' }),
      createTextColumn<AimIntegrationDraft>({
        key: 'comment',
        title: 'Comment',
      }),
      createFrontendOnlyColumn<AimIntegrationDraft>({
        key: 'actions',
        disabledSort: true,
        title: 'Actions',
        render: (row) => (
          <div className='flex flex-row items-center gap-xs'>
            <TableButton
              variant='plain'
              icon='fa-pen'
              size='small'
              onClick={() => actions.handleEdit(row)}
              type='button'
            />
            <TableButton
              variant='plain'
              accent='negative'
              icon='fa-trash-can'
              size='small'
              onClick={() => actions.handleDelete(row.id)}
              type='button'
            />
          </div>
        ),
      }),
    ],
    [actions],
  );

  const bulkActions: TableBulkAction[] = useMemo(
    () => [
      {
        key: 'delete',
        title: 'Delete',
        accent: 'negative',
        icon: 'fa-trash-can',
        disableAutoDeselect: true,
        onClick: async ({ selectedRows, deselectAll }) => {
          const ok = await actions.handleBulkDelete(
            unsafeAssert<Set<unknown>, Set<string>>(selectedRows),
          );
          if (ok) {
            deselectAll();
          }
        },
      },
    ],
    [actions],
  );

  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: integrations,
  });

  return (
    <Card className='flex flex-col flex-1 rounded-tl-none' noShadow>
      <Table
        borderless
        bulkActions={bulkActions}
        rowIdField='id'
        loadRowIdsForSelectAll='fromData'
        className='flex-1 bg-transparent'
        columns={columns}
        rowHeight={44}
        title='Integration providers'
        menuItems={[
          {
            standalone: true,
            elementWithKey: (
              <Button
                key='add-new'
                variant='outline'
                size='small'
                icon='fa-plus-circle'
                onClick={actions.handleAdd}
              >
                Add new
              </Button>
            ),
          },
        ]}
        {...frontendSimpleFilter}
      />
      {actions.editedItem !== null && (
        <AimIntegrationDialog
          formType={actions.editedItem === 'new' ? 'add' : 'edit'}
          initialValues={
            actions.editedItem === 'new' ? undefined : actions.editedItem
          }
          hasExistingApiSecret={
            actions.editedItem !== 'new' && !!actions.editedItem.apiSecret
          }
          existingIntegrations={integrations}
          onClose={actions.handleCloseDialog}
          onSubmit={actions.handleSubmitDialog}
        />
      )}
      {actions.confirmDeleteDialogRenderer()}
    </Card>
  );
};
